import React, { useState, useEffect } from 'react';
import './index.css';
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Input,
  Button,
  Grid,
  Box,
  RadioGroup,
  Spinner,
  Flashbar,
  ExpandableSection
} from '@cloudscape-design/components';
import { submissionsApi } from '../../api/Submission';
import { transformFormDataToAPI } from '../../utils/formDataTransformer';
import { useBreadcrumbs } from '../../components/breadcrumbs';

// Institution licence dates are entered/displayed as DD/MM/YY (2-digit year).
// Century rule (kept in sync with formatDateForAPI): 00-69 -> 2000-2069, 70-99 -> 1970-1999.
const parseDisplayDate = (value) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{2})$/.exec((value || '').trim());
  if (!match) return null;
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const yy = parseInt(match[3], 10);
  const year = yy <= 69 ? 2000 + yy : 1900 + yy;
  const date = new Date(year, month - 1, day);
  // Reject impossible dates (e.g. 31/02/20) via a round-trip check.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
};

// "YYYY-MM-DD" (from the API) -> "DD/MM/YY" for display.
const apiDateToDisplay = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return '';
  return `${day}/${month}/${year.slice(-2)}`;
};

const InstituteProfileForm = () => {
  const { setActiveBreadcrumb } = useBreadcrumbs();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profileExists, setProfileExists] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    instituteNameAr: '',
    instituteNameEn: '',
    licensingBody: '',
    yearOfEstablishment: '',
    licenseNumber: '',
    issueDate: '',
    expiryDate: '',
    ownerName: '',
    ownerMobile: '',
    ownerEmail: '',
    executiveName: '',
    executiveMobile: '',
    executiveEmail: '',
    nomineeName: '',
    nomineeMobile: '',
    nomineeEmail: '',
    instituteEmail: '',
    instituteWebsite: '',
    socialMediaAccount: '',
    instituteAddress: '',
    localAttendance: (() => {
      const currentYear = new Date().getFullYear();
      const data = {};
      for (let i = 3; i >= 0; i--) {
        const year = currentYear - i;
        data[year] = { male: '', female: '', total: '0' };
      }
      return data;
    })(),
    localAchievement: (() => {
      const currentYear = new Date().getFullYear();
      const data = {};
      for (let i = 3; i >= 0; i--) {
        const year = currentYear - i;
        data[year] = { male: '', female: '', total: '0' };
      }
      return data;
    })(),
    externalAchievement: (() => {
      const currentYear = new Date().getFullYear();
      const data = {};
      for (let i = 3; i >= 0; i--) {
        const year = currentYear - i;
        data[year] = { male: '', female: '', total: '0' };
      }
      return data;
    })(),
    nqfStatus: '',
    nqfQualifications: [
      { qualification: '', nqfLevel: '' },
      { qualification: '', nqfLevel: '' },
      { qualification: '', nqfLevel: '' },
      { qualification: '', nqfLevel: '' },
      { qualification: '', nqfLevel: '' }
    ],
    generalInformation: '',
    mainCharacteristics: '',
    effectivenessFeatures: '',
    serviceEmployeeNameEn: '',
    serviceEmployeeNameAr: '',
    serviceEmployeeCpr: '',
    serviceEmployeePosition: '',
    serviceEmployeeOffice: '',
    serviceEmployeeMobile: '',
    serviceEmployeeEmail: ''
  });

  const [errors, setErrors] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    localAttendance: false,
    localAchievement: false,
    externalAchievement: false
  });

  // Notification helpers
  const addNotification = (type, content) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, {
      id,
      type,
      content,
      dismissible: true,
      onDismiss: () => setNotifications(prev => prev.filter(n => n.id !== id))
    }]);
  };

  const showSuccess = (message) => addNotification('success', message);
  const showError = (message) => addNotification('error', message);

  // Load existing profile data on component mount
  useEffect(() => {
    setActiveBreadcrumb({ text: 'Institution Profile', href: '/institution-profile', index: 1 }, 1);
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setIsLoading(true);
      console.log('Loading profile data...');
      const response = await submissionsApi.getInstitutionProfile();
      console.log('Profile API response:', response);
      
      if (response && response.id) {
        // Profile exists, populate form with existing data
        console.log('Profile exists, populating form with:', response);
        setProfileExists(true);
        setIsEditMode(false); // View mode by default
        populateFormWithProfileData(response);
      } else {
        // No profile exists, keep empty form
        console.log('No profile found, keeping empty form');
        setProfileExists(false);
        setIsEditMode(true); // Edit mode for new profile
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setProfileExists(false);
    } finally {
      setIsLoading(false);
    }
  };

  const populateFormWithProfileData = (profileData) => {
    const { submission, contacts, local_attendance, local_achievement, external_achievement } = profileData;
    
    // Populate basic submission data
    if (submission) {
      setFormData(prev => ({
        ...prev,
        instituteNameEn: submission.institute_name_en || '',
        instituteNameAr: submission.institute_name_ar || '',
        licensingBody: submission.licensing_body || '',
        yearOfEstablishment: submission.year_of_establishment?.toString() || '',
        licenseNumber: submission.license_number || '',
        issueDate: apiDateToDisplay(submission.license_issue_date),
        expiryDate: apiDateToDisplay(submission.license_expiry_date),
        instituteEmail: submission.institute_email || '',
        instituteWebsite: submission.institute_website || '',
        socialMediaAccount: submission.social_media?.[0] || '',
        instituteAddress: submission.institute_address || '',
        nqfStatus: submission.nqf_status || '',
        nqfQualifications: (() => {
          if (!submission.qualifications_on_nqf) {
            return [
              { qualification: '', nqfLevel: '' },
              { qualification: '', nqfLevel: '' },
              { qualification: '', nqfLevel: '' },
              { qualification: '', nqfLevel: '' },
              { qualification: '', nqfLevel: '' }
            ];
          }
          try {
            return JSON.parse(submission.qualifications_on_nqf);
          } catch (error) {
            // Handle old string format - put it in first row
            return [
              { qualification: submission.qualifications_on_nqf, nqfLevel: '' },
              { qualification: '', nqfLevel: '' },
              { qualification: '', nqfLevel: '' },
              { qualification: '', nqfLevel: '' },
              { qualification: '', nqfLevel: '' }
            ];
          }
        })(),
        generalInformation: submission.general_information || '',
        mainCharacteristics: submission.main_characteristics || '',
        effectivenessFeatures: submission.effectiveness_features || '',
        serviceEmployeeNameEn: submission.service_employee_name_en || '',
        serviceEmployeeNameAr: submission.service_employee_name_ar || '',
        serviceEmployeeCpr: submission.service_employee_cpr || '',
        serviceEmployeePosition: submission.service_employee_position || '',
        serviceEmployeeOffice: submission.service_employee_office || '',
        serviceEmployeeMobile: submission.service_employee_mobile || '',
        serviceEmployeeEmail: submission.service_employee_email || ''
      }));
    }
    
    // Populate contact data
    if (contacts && contacts.length > 0) {
      const contactUpdates = {};
      contacts.forEach(contact => {
        if (contact.role === 'Owner') {
          contactUpdates.ownerName = contact.name || '';
          contactUpdates.ownerMobile = contact.mobile_no || '';
          contactUpdates.ownerEmail = contact.email || '';
        } else if (contact.role === 'CEO') {
          contactUpdates.executiveName = contact.name || '';
          contactUpdates.executiveMobile = contact.mobile_no || '';
          contactUpdates.executiveEmail = contact.email || '';
        } else if (contact.role === 'Nominee') {
          contactUpdates.nomineeName = contact.name || '';
          contactUpdates.nomineeMobile = contact.mobile_no || '';
          contactUpdates.nomineeEmail = contact.email || '';
        }
      });
      setFormData(prev => ({ ...prev, ...contactUpdates }));
    }
    
    // Populate qualification data
    if (local_attendance && local_attendance.length > 0) {
      const attendanceUpdates = { ...formData.localAttendance };
      local_attendance.forEach(record => {
        const year = record.year;
        if (attendanceUpdates[year]) {
          attendanceUpdates[year] = {
            male: record.male_count?.toString() || '',
            female: record.female_count?.toString() || '',
            total: record.total_count?.toString() || '0'
          };
        }
      });
      setFormData(prev => ({ ...prev, localAttendance: attendanceUpdates }));
    }
    
    if (local_achievement && local_achievement.length > 0) {
      const achievementUpdates = { ...formData.localAchievement };
      local_achievement.forEach(record => {
        const year = record.year;
        if (achievementUpdates[year]) {
          achievementUpdates[year] = {
            male: record.male_count?.toString() || '',
            female: record.female_count?.toString() || '',
            total: record.total_count?.toString() || '0'
          };
        }
      });
      setFormData(prev => ({ ...prev, localAchievement: achievementUpdates }));
    }
    
    if (external_achievement && external_achievement.length > 0) {
      const externalUpdates = { ...formData.externalAchievement };
      external_achievement.forEach(record => {
        const year = record.year;
        if (externalUpdates[year]) {
          externalUpdates[year] = {
            male: record.male_count?.toString() || '',
            female: record.female_count?.toString() || '',
            total: record.total_count?.toString() || '0'
          };
        }
      });
      setFormData(prev => ({ ...prev, externalAchievement: externalUpdates }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Required fields
    if (!formData.instituteNameEn?.trim()) {
      newErrors.instituteNameEn = 'English institute name is required';
    } else if (formData.instituteNameEn.length > 255) {
      newErrors.instituteNameEn = 'Institute name must be less than 255 characters';
    }

    if (!formData.instituteNameAr?.trim()) {
      newErrors.instituteNameAr = 'Arabic institute name is required';
    } else if (formData.instituteNameAr.length > 255) {
      newErrors.instituteNameAr = 'Institute name must be less than 255 characters';
    } else {
      // Check if text contains Arabic characters
      const arabicRegex = /[\u0600-\u06FF]/;
      if (!arabicRegex.test(formData.instituteNameAr)) {
        newErrors.instituteNameAr = 'Arabic institute name must contain Arabic characters';
      }
    }

    // All other required fields
    if (!formData.licensingBody?.trim()) newErrors.licensingBody = 'Licensing body is required';
    if (!formData.yearOfEstablishment?.trim()) newErrors.yearOfEstablishment = 'Year of establishment is required';
    if (!formData.licenseNumber?.trim()) newErrors.licenseNumber = 'License number is required';
    if (!formData.issueDate?.trim()) newErrors.issueDate = 'Issue date is required';
    if (!formData.expiryDate?.trim()) newErrors.expiryDate = 'Expiry date is required';
    if (!formData.ownerName?.trim()) newErrors.ownerName = 'Owner name is required';
    if (!formData.ownerMobile?.trim()) newErrors.ownerMobile = 'Owner mobile is required';
    if (!formData.ownerEmail?.trim()) newErrors.ownerEmail = 'Owner email is required';
    if (!formData.executiveName?.trim()) newErrors.executiveName = 'Executive name is required';
    if (!formData.executiveMobile?.trim()) newErrors.executiveMobile = 'Executive mobile is required';
    if (!formData.executiveEmail?.trim()) newErrors.executiveEmail = 'Executive email is required';
    if (!formData.nomineeName?.trim()) newErrors.nomineeName = 'Nominee name is required';
    if (!formData.nomineeMobile?.trim()) newErrors.nomineeMobile = 'Nominee mobile is required';
    if (!formData.nomineeEmail?.trim()) newErrors.nomineeEmail = 'Nominee email is required';
    if (!formData.instituteEmail?.trim()) newErrors.instituteEmail = 'Institute email is required';
    if (!formData.instituteWebsite?.trim()) newErrors.instituteWebsite = 'Institute website is required';
    if (!formData.socialMediaAccount?.trim()) newErrors.socialMediaAccount = 'Social media account is required';
    if (!formData.instituteAddress?.trim()) newErrors.instituteAddress = 'Institute address is required';
    if (!formData.nqfStatus?.trim()) newErrors.nqfStatus = 'NQF status is required';
    if (!formData.generalInformation?.trim()) newErrors.generalInformation = 'General information is required';
    if (!formData.mainCharacteristics?.trim()) newErrors.mainCharacteristics = 'Main characteristics is required';
    if (!formData.effectivenessFeatures?.trim()) newErrors.effectivenessFeatures = 'Effectiveness features is required';
    if (!formData.serviceEmployeeNameEn?.trim()) newErrors.serviceEmployeeNameEn = 'Employee name (English) is required';
    if (!formData.serviceEmployeeNameAr?.trim()) newErrors.serviceEmployeeNameAr = 'Employee name (Arabic) is required';
    if (!formData.serviceEmployeeCpr?.trim()) newErrors.serviceEmployeeCpr = 'Employee CPR is required';
    if (!formData.serviceEmployeePosition?.trim()) newErrors.serviceEmployeePosition = 'Employee position is required';
    if (!formData.serviceEmployeeOffice?.trim()) newErrors.serviceEmployeeOffice = 'Employee office is required';
    if (!formData.serviceEmployeeMobile?.trim()) newErrors.serviceEmployeeMobile = 'Employee mobile is required';
    if (!formData.serviceEmployeeEmail?.trim()) newErrors.serviceEmployeeEmail = 'Employee email is required';

    // String length validations
    if (formData.licensingBody && formData.licensingBody.length > 255) {
      newErrors.licensingBody = 'Licensing body must be less than 255 characters';
    }
    if (formData.licenseNumber && formData.licenseNumber.length > 100) {
      newErrors.licenseNumber = 'License number must be less than 100 characters';
    }

    // Year validation
    if (formData.yearOfEstablishment) {
      const year = parseInt(formData.yearOfEstablishment);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1800 || year > currentYear) {
        newErrors.yearOfEstablishment = `Year must be between 1800 and ${currentYear}`;
      }
    }

    // Date format validation (DD/MM/YY) - each part must be exactly 2 digits
    const dateRegex = /^\d{2}\/\d{2}\/\d{2}$/;

    if (formData.issueDate) {
      if (!dateRegex.test(formData.issueDate)) {
        newErrors.issueDate = 'Date must be in DD/MM/YY format (e.g., 25/12/20)';
      } else if (!parseDisplayDate(formData.issueDate)) {
        newErrors.issueDate = 'Please enter a valid date';
      }
    }

    if (formData.expiryDate) {
      if (!dateRegex.test(formData.expiryDate)) {
        newErrors.expiryDate = 'Date must be in DD/MM/YY format (e.g., 25/12/25)';
      } else if (!parseDisplayDate(formData.expiryDate)) {
        newErrors.expiryDate = 'Please enter a valid date';
      }
    }
    
    // Date comparison validation
    if (formData.issueDate && formData.expiryDate && !newErrors.issueDate && !newErrors.expiryDate) {
      const issueDate = parseDisplayDate(formData.issueDate);
      const expiryDate = parseDisplayDate(formData.expiryDate);
      if (issueDate && expiryDate && expiryDate <= issueDate) {
        newErrors.expiryDate = 'Expiry date must be after issue date';
      }
    }

    // Email validations
    ['ownerEmail', 'executiveEmail', 'nomineeEmail', 'instituteEmail'].forEach(field => {
      if (formData[field]) {
        if (!emailRegex.test(formData[field])) {
          newErrors[field] = 'Invalid email format';
        } else if (formData[field].length > 255) {
          newErrors[field] = 'Email must be less than 255 characters';
        }
      }
    });

    // Phone number validation (Bahrain mobile or internal extension)
    const phoneValidationRegex = /^(?:\+973|00973)?3[2-9]\d{6}$|^\d{3,5}$/;
    ['ownerMobile', 'executiveMobile', 'nomineeMobile', 'serviceEmployeeMobile'].forEach(field => {
      if (formData[field]) {
        if (!phoneValidationRegex.test(formData[field])) {
          newErrors[field] = 'Enter a valid Bahrain mobile number (+97339000xxxx, 39000xxxx) or internal extension (101-45012)';
        }
      }
    });

    // Name validations
    ['ownerName', 'executiveName', 'nomineeName'].forEach(field => {
      if (formData[field] && formData[field].length > 255) {
        newErrors[field] = 'Name must be less than 255 characters';
      }
    });

    // Website validation
    if (formData.instituteWebsite && formData.instituteWebsite.length > 255) {
      newErrors.instituteWebsite = 'Website URL must be less than 255 characters';
    }

    if (formData.instituteAddress && formData.instituteAddress.length > 500) {
      newErrors.instituteAddress = 'Address must be less than 500 characters';
    }

    // NQF Status validation
    if (formData.nqfStatus && !['Listed', 'Not Listed', 'In progress'].includes(formData.nqfStatus)) {
      newErrors.nqfStatus = 'NQF Status must be: Listed, Not Listed, or In progress';
    }

    // Text field validations (250 words max)
    const wordCount = (text) => text ? text.trim().split(/\s+/).length : 0;
    
    if (formData.generalInformation && wordCount(formData.generalInformation) > 250 && process.env.REACT_APP_FRAMEWORK == 2) {
      newErrors.generalInformation = 'Maximum 250 words allowed';
    }
    
    if (formData.mainCharacteristics && wordCount(formData.mainCharacteristics) > 250  && process.env.REACT_APP_FRAMEWORK == 2) {
      newErrors.mainCharacteristics = 'Maximum 250 words allowed';
    }
    
    if (formData.effectivenessFeatures && wordCount(formData.effectivenessFeatures) > 250  && process.env.REACT_APP_FRAMEWORK == 2) {
      newErrors.effectivenessFeatures = 'Maximum 250 words allowed';
    }

    // Additional Arabic validation for employee name
    if (formData.serviceEmployeeNameAr) {
      const arabicRegex = /[\u0600-\u06FF]/;
      if (!arabicRegex.test(formData.serviceEmployeeNameAr)) {
        newErrors.serviceEmployeeNameAr = 'Employee name (Arabic) must contain Arabic characters';
      }
    }
    


    // Qualification validation - make all fields mandatory
    ['localAttendance', 'localAchievement', 'externalAchievement'].forEach(type => {
      Object.keys(formData[type]).forEach(year => {
        const male = formData[type][year].male;
        const female = formData[type][year].female;
        
        // Make male and female fields mandatory
        if (!male?.trim()) {
          newErrors[`${type}_${year}_male`] = 'Male count is required';
        } else if (isNaN(parseInt(male)) || parseInt(male) < 0) {
          newErrors[`${type}_${year}_male`] = 'Must be a positive number';
        }
        
        if (!female?.trim()) {
          newErrors[`${type}_${year}_female`] = 'Female count is required';
        } else if (isNaN(parseInt(female)) || parseInt(female) < 0) {
          newErrors[`${type}_${year}_female`] = 'Must be a positive number';
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleQualificationChange = (type, year, gender, value) => {
    setFormData(prev => {
      const newData = { ...prev[type] };
      newData[year] = { ...newData[year], [gender]: value };
      
      if (gender === 'male' || gender === 'female') {
        const male = parseInt(newData[year].male || 0);
        const female = parseInt(newData[year].female || 0);
        newData[year].total = (male + female).toString();
      }
      
      return { ...prev, [type]: newData };
    });
    
    // Clear error for this field when user types
    const errorKey = `${type}_${year}_${gender}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // Check if there are qualification errors and expand the relevant sections
      const qualificationErrors = Object.keys(errors).filter(key => 
        key.startsWith('localAttendance_') || 
        key.startsWith('localAchievement_') || 
        key.startsWith('externalAchievement_')
      );
      
      if (qualificationErrors.length > 0) {
        const newExpandedSections = { ...expandedSections };
        qualificationErrors.forEach(errorKey => {
          if (errorKey.startsWith('localAttendance_')) newExpandedSections.localAttendance = true;
          if (errorKey.startsWith('localAchievement_')) newExpandedSections.localAchievement = true;
          if (errorKey.startsWith('externalAchievement_')) newExpandedSections.externalAchievement = true;
        });
        setExpandedSections(newExpandedSections);
      }
      
      // Scroll to first error field
      setTimeout(() => {
        const firstErrorField = 
          document.querySelector('[data-awsui-error-text]') ||
          document.querySelector('.awsui-form-field-error-text') ||
          document.querySelector('.awsui-form-field[data-invalid]') ||
          document.querySelector('input[aria-invalid="true"]');
          
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 200);
      return;
    }

    setIsSubmitting(true);
    try {
      const transformedData = transformFormDataToAPI(formData);
      
      if (profileExists) {
        // Update existing profile
        await submissionsApi.updateInstitutionProfile(transformedData);
        setIsEditMode(false); // Exit edit mode
        showSuccess('Profile updated successfully!');
      } else {
        // Create new profile
        await submissionsApi.createInstitutionProfile(transformedData);
        setProfileExists(true); // Mark as existing for future updates
        setIsEditMode(false); // Exit edit mode
        showSuccess('Profile created successfully!');
      }
    } catch (error) {
      console.error('Submission error:', error);
      showError('Failed to save profile: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
  <Container className="institution-profile-page">
      <SpaceBetween size="m">
        <Flashbar items={notifications} />
        <Header 
          variant="h1"
          actions={
            profileExists && !isEditMode ? (
              <Button 
                variant="primary" 
                onClick={() => setIsEditMode(true)}
              >
                Update Profile
              </Button>
            ) : null
          }
        >
          {isLoading ? 'Loading Profile...' : 'Institution Profile'}
        </Header>
            
            {isLoading ? (
              <Box textAlign="center">
                <Spinner size="large" />
                <Box variant="p">Loading your profile data...</Box>
              </Box>
            ) : (
              <>
            {/* Basic Information */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField
                label={<span className="field-label">Name of the Institute (Arabic)</span>}
                errorText={errors.instituteNameAr}
              >
                <Input
                  value={formData.instituteNameAr}
                  onChange={({ detail }) => handleInputChange('instituteNameAr', detail.value)}
                  placeholder="Placeholder"
                  readOnly={!isEditMode}
                />
              </FormField>
              
              <FormField
                label={<span className="field-label">Name of the Institute (English)</span>}
                errorText={errors.instituteNameEn}
              >
                <Input
                  value={formData.instituteNameEn}
                  onChange={({ detail }) => handleInputChange('instituteNameEn', detail.value)}
                  placeholder="Placeholder"
                  readOnly={!isEditMode}
                />
              </FormField>
            </Grid>

            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField
                label={<span className="field-label">Licensing Body</span>}
                errorText={errors.licensingBody}
              >
                <Input
                  value={formData.licensingBody}
                  onChange={({ detail }) => handleInputChange('licensingBody', detail.value)}
                  placeholder="Placeholder"
                  readOnly={!isEditMode}
                />
              </FormField>
              
              <FormField
                label={<span className="field-label">Year of Establishment</span>}
                errorText={errors.yearOfEstablishment}
              >
                <Input
                  value={formData.yearOfEstablishment}
                  onChange={({ detail }) => handleInputChange('yearOfEstablishment', detail.value)}
                  placeholder="Placeholder"
                  readOnly={!isEditMode}
                />
              </FormField>
            </Grid>

            {/* License Information */}
            <Container header={<Header variant="h2">The Institute's License</Header>} className="section-container">
              <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                <FormField
                  label={<span className="field-label">License Number</span>}
                  errorText={errors.licenseNumber}
                >
                  <Input
                    value={formData.licenseNumber}
                    onChange={({ detail }) => handleInputChange('licenseNumber', detail.value)}
                    placeholder="Placeholder"
                    readOnly={!isEditMode}
                  />
                </FormField>
                
                <FormField
                  label={<span className="field-label">Date of Issue</span>}
                  errorText={errors.issueDate}
                >
                  <Input
                    value={formData.issueDate}
                    onChange={({ detail }) => handleInputChange('issueDate', detail.value)}
                    placeholder="DD/MM/YY (e.g., 25/12/20)"
                    readOnly={!isEditMode}
                  />
                </FormField>
                
                <FormField
                  label={<span className="field-label">Date of Expiry</span>}
                  errorText={errors.expiryDate}
                >
                  <Input
                    value={formData.expiryDate}
                    onChange={({ detail }) => handleInputChange('expiryDate', detail.value)}
                    placeholder="DD/MM/YY (e.g., 25/12/25)"
                    readOnly={!isEditMode}
                  />
                </FormField>
              </Grid>
            </Container>

            {/* Contact Sections */}
            <Container header={<Header variant="h2">The Owner's Contact Details</Header>} className="section-container">
              <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                <FormField label={<span className="field-label">Name</span>} errorText={errors.ownerName}>
                  <Input value={formData.ownerName} onChange={({ detail }) => handleInputChange('ownerName', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
                </FormField>
                <FormField label={<span className="field-label">Mobile No.</span>} errorText={errors.ownerMobile}>
                  <Input value={formData.ownerMobile} onChange={({ detail }) => handleInputChange('ownerMobile', detail.value)} placeholder="+97339000xxxx or 39000xxxx or 101" readOnly={!isEditMode} />
                </FormField>
                <FormField label={<span className="field-label">Email Address</span>} errorText={errors.ownerEmail}>
                  <Input type="email" value={formData.ownerEmail} onChange={({ detail }) => handleInputChange('ownerEmail', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
                </FormField>
              </Grid>
            </Container>

            <Container header={<Header variant="h2">The Chief Executive/Managing Director's Contact Details</Header>} className="section-container">
              <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                <FormField label={<span className="field-label">Name</span>} errorText={errors.executiveName}>
                  <Input value={formData.executiveName} onChange={({ detail }) => handleInputChange('executiveName', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
                </FormField>
                <FormField label={<span className="field-label">Mobile No.</span>} errorText={errors.executiveMobile}>
                  <Input value={formData.executiveMobile} onChange={({ detail }) => handleInputChange('executiveMobile', detail.value)} placeholder="+97339000xxxx or 39000xxxx or 101" readOnly={!isEditMode} />
                </FormField>
                <FormField label={<span className="field-label">Email Address</span>} errorText={errors.executiveEmail}>
                  <Input type="email" value={formData.executiveEmail} onChange={({ detail }) => handleInputChange('executiveEmail', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
                </FormField>
              </Grid>
            </Container>

            <Container header={<Header variant="h2">The Nominee Contact Details</Header>} className="section-container">
              <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                <FormField label={<span className="field-label">Name</span>} errorText={errors.nomineeName}>
                  <Input value={formData.nomineeName} onChange={({ detail }) => handleInputChange('nomineeName', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
                </FormField>
                <FormField label={<span className="field-label">Mobile No.</span>} errorText={errors.nomineeMobile}>
                  <Input value={formData.nomineeMobile} onChange={({ detail }) => handleInputChange('nomineeMobile', detail.value)} placeholder="+97339000xxxx or 39000xxxx or 101" readOnly={!isEditMode} />
                </FormField>
                <FormField label={<span className="field-label">Email Address</span>} errorText={errors.nomineeEmail}>
                  <Input type="email" value={formData.nomineeEmail} onChange={({ detail }) => handleInputChange('nomineeEmail', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
                </FormField>
              </Grid>
            </Container>

            {/* Institute Contact Information */}
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
              <FormField label={<span className="field-label">The Institute's Email</span>} errorText={errors.instituteEmail}>
                <Input type="email" value={formData.instituteEmail} onChange={({ detail }) => handleInputChange('instituteEmail', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
              </FormField>
              <FormField label={<span className="field-label">The Institute's Website</span>} errorText={errors.instituteWebsite}>
                <Input value={formData.instituteWebsite} onChange={({ detail }) => handleInputChange('instituteWebsite', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
              </FormField>
              <FormField label={<span className="field-label">Social Media Account</span>} errorText={errors.socialMediaAccount}>
                <Input value={formData.socialMediaAccount} onChange={({ detail }) => handleInputChange('socialMediaAccount', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
              </FormField>
              <FormField label={<span className="field-label">The Institute's Address</span>} errorText={errors.instituteAddress}>
                <Input value={formData.instituteAddress} onChange={({ detail }) => handleInputChange('instituteAddress', detail.value)} placeholder="Placeholder" readOnly={!isEditMode} />
              </FormField>
            </Grid>

            {/* Qualification Profile */}
            <Container header={<Header variant="h2">Qualification Profile (By Qualification Type, Year and Gender) for the Last Four Years</Header>} className="section-container">
              <SpaceBetween size="s">
                {/* Local Attendance */}
                <ExpandableSection 
                  headerText="Local Attendance" 
                  expanded={expandedSections.localAttendance}
                  onChange={({ detail }) => setExpandedSections(prev => ({ ...prev, localAttendance: detail.expanded }))}
                  variant="borderless"
                >
                  <SpaceBetween size="xs">
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      const years = [];
                      for (let i = 3; i >= 0; i--) {
                        years.push(currentYear - i);
                      }
                      return years;
                    })().map(year => (
                      <div key={`local_attendance_${year}`}>
                        <Box variant="h4" margin={{ bottom: 'xs' }}>{year}</Box>
                        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                          <FormField label="Male" errorText={errors[`localAttendance_${year}_male`]}>
                            <Input 
                              value={formData.localAttendance[year]?.male || ''} 
                              onChange={({ detail }) => handleQualificationChange('localAttendance', year, 'male', detail.value)} 
                              placeholder="0"
                              readOnly={!isEditMode}
                            />
                          </FormField>
                          <FormField label="Female" errorText={errors[`localAttendance_${year}_female`]}>
                            <Input 
                              value={formData.localAttendance[year]?.female || ''} 
                              onChange={({ detail }) => handleQualificationChange('localAttendance', year, 'female', detail.value)} 
                              placeholder="0"
                              readOnly={!isEditMode}
                            />
                          </FormField>
                          <FormField label="Total">
                            <Input 
                              value={formData.localAttendance[year]?.total || '0'} 
                              placeholder="0" 
                              readOnly 
                            />
                          </FormField>
                        </Grid>
                      </div>
                    ))}
                  </SpaceBetween>
                </ExpandableSection>

                {/* Local Achievement */}
                <ExpandableSection 
                  headerText="Local Achievement" 
                  expanded={expandedSections.localAchievement}
                  onChange={({ detail }) => setExpandedSections(prev => ({ ...prev, localAchievement: detail.expanded }))}
                  variant="borderless"
                >
                  <SpaceBetween size="xs">
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      const years = [];
                      for (let i = 3; i >= 0; i--) {
                        years.push(currentYear - i);
                      }
                      return years;
                    })().map(year => (
                      <div key={`local_achievement_${year}`}>
                        <Box variant="h4" margin={{ bottom: 'xs' }}>{year}</Box>
                        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                          <FormField label="Male" errorText={errors[`localAchievement_${year}_male`]}>
                            <Input 
                              value={formData.localAchievement[year]?.male || ''} 
                              onChange={({ detail }) => handleQualificationChange('localAchievement', year, 'male', detail.value)} 
                              placeholder="0"
                              readOnly={!isEditMode}
                            />
                          </FormField>
                          <FormField label="Female" errorText={errors[`localAchievement_${year}_female`]}>
                            <Input 
                              value={formData.localAchievement[year]?.female || ''} 
                              onChange={({ detail }) => handleQualificationChange('localAchievement', year, 'female', detail.value)} 
                              placeholder="0"
                              readOnly={!isEditMode}
                            />
                          </FormField>
                          <FormField label="Total">
                            <Input 
                              value={formData.localAchievement[year]?.total || '0'} 
                              placeholder="0" 
                              readOnly 
                            />
                          </FormField>
                        </Grid>
                      </div>
                    ))}
                  </SpaceBetween>
                </ExpandableSection>

                {/* External Achievement */}
                <ExpandableSection 
                  headerText="External Achievement" 
                  expanded={expandedSections.externalAchievement}
                  onChange={({ detail }) => setExpandedSections(prev => ({ ...prev, externalAchievement: detail.expanded }))}
                  variant="borderless"
                >
                  <SpaceBetween size="xs">
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      const years = [];
                      for (let i = 3; i >= 0; i--) {
                        years.push(currentYear - i);
                      }
                      return years;
                    })().map(year => (
                      <div key={`external_achievement_${year}`}>
                        <Box variant="h4" margin={{ bottom: 'xs' }}>{year}</Box>
                        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                          <FormField label="Male" errorText={errors[`externalAchievement_${year}_male`]}>
                            <Input 
                              value={formData.externalAchievement[year]?.male || ''} 
                              onChange={({ detail }) => handleQualificationChange('externalAchievement', year, 'male', detail.value)} 
                              placeholder="0"
                              readOnly={!isEditMode}
                            />
                          </FormField>
                          <FormField label="Female" errorText={errors[`externalAchievement_${year}_female`]}>
                            <Input 
                              value={formData.externalAchievement[year]?.female || ''} 
                              onChange={({ detail }) => handleQualificationChange('externalAchievement', year, 'female', detail.value)} 
                              placeholder="0"
                              readOnly={!isEditMode}
                            />
                          </FormField>
                          <FormField label="Total">
                            <Input 
                              value={formData.externalAchievement[year]?.total || '0'} 
                              placeholder="0" 
                              readOnly 
                            />
                          </FormField>
                        </Grid>
                      </div>
                    ))}
                  </SpaceBetween>
                </ExpandableSection>
              </SpaceBetween>
            </Container>

            {/* NQF Section */}
            <SpaceBetween size="s">
              <FormField
                label={<span className="field-label">Institutional Listing Status on The National Qualification Framework (NQF)</span>}
                errorText={errors.nqfStatus}
              >
                <RadioGroup
                  value={formData.nqfStatus}
                  onChange={({ detail }) => handleInputChange('nqfStatus', detail.value)}
                  items={[
                    { value: 'Listed', label: 'Listed' },
                    { value: 'Not Listed', label: 'Not Listed' },
                    { value: 'In progress', label: 'In progress' }
                  ]}
                  readOnly={!isEditMode}
                />
              </FormField>
              
              <FormField
                label={<span className="field-label">List of Qualifications Placed on/Aligned to the NQF (If Applicable)</span>}
                description="(Add NA for not applicable fields)"
              >
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Qualification</th>
                      <th style={{ padding: '8px', width: '150px', textAlign: 'left' }}>NQF Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.nqfQualifications.map((row, index) => (
                      <tr key={index}>
                        <td style={{ padding: '4px' }}>
                          <Input
                            value={row.qualification}
                            onChange={({ detail }) => {
                              const newQualifications = [...formData.nqfQualifications];
                              newQualifications[index].qualification = detail.value;
                              setFormData(prev => ({ ...prev, nqfQualifications: newQualifications }));
                            }}
                            placeholder="Enter qualification"
                            readOnly={!isEditMode}
                          />
                        </td>
                        <td style={{ padding: '4px' }}>
                          <Input
                            value={row.nqfLevel}
                            onChange={({ detail }) => {
                              const newQualifications = [...formData.nqfQualifications];
                              newQualifications[index].nqfLevel = detail.value;
                              setFormData(prev => ({ ...prev, nqfQualifications: newQualifications }));
                            }}
                            placeholder="Enter NQF level"
                            readOnly={!isEditMode}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </FormField>
            </SpaceBetween>

            {/* Summary of Vocational Offering and Strategic Focus */}
            <Container header={<Header variant="h2">Summary of Vocational Offering and Strategic Focus</Header>} className="section-container">
              <SpaceBetween size="l">
                <FormField
                  label={<span className="field-label">1. General information about the Institute (Maximum 250 words)</span>}
                  errorText={errors.generalInformation}
                >
                  <textarea
                    value={formData.generalInformation}
                    onChange={(e) => handleInputChange('generalInformation', e.target.value)}
                    placeholder="Enter general information about the institute..."
                    rows={6}
                    disabled={!isEditMode}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      resize: 'vertical',
                      backgroundColor: !isEditMode ? '#f5f5f5' : 'white'
                    }}
                  />
                </FormField>
                
                <FormField
                  label={<span className="field-label">2. Main characteristics of the provision including any major changes since the last review visit, if applicable (Maximum 250 words)</span>}
                  errorText={errors.mainCharacteristics}
                >
                  <textarea
                    value={formData.mainCharacteristics}
                    onChange={(e) => handleInputChange('mainCharacteristics', e.target.value)}
                    placeholder="Enter main characteristics of the provision..."
                    rows={6}
                    disabled={!isEditMode}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      resize: 'vertical',
                      backgroundColor: !isEditMode ? '#f5f5f5' : 'white'
                    }}
                  />
                </FormField>
                
                <FormField
                  label={<span className="field-label">3. Challenges that affect the effectiveness of the provision (Maximum 250 words)</span>}
                  errorText={errors.effectivenessFeatures}
                >
                  <textarea
                    value={formData.effectivenessFeatures}
                    onChange={(e) => handleInputChange('effectivenessFeatures', e.target.value)}
                    placeholder="Enter features that aid or inhibit effectiveness..."
                    rows={6}
                    disabled={!isEditMode}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      resize: 'vertical',
                      backgroundColor: !isEditMode ? '#f5f5f5' : 'white'
                    }}
                  />
                </FormField>
              </SpaceBetween>
            </Container>

            {/* Details of the person responsible staff for the service charges */}
            <Container header={<Header variant="h2">Details of the person responsible staff for the service charges</Header>} className="section-container">
              <SpaceBetween size="l">
                <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                  <FormField
                    label={<span className="field-label">Name of the Employee (English) <span className="required">*</span></span>}
                    errorText={errors.serviceEmployeeNameEn}
                  >
                    <Input
                      value={formData.serviceEmployeeNameEn}
                      onChange={({ detail }) => handleInputChange('serviceEmployeeNameEn', detail.value)}
                      placeholder="Enter employee name in English"
                      readOnly={!isEditMode}
                    />
                  </FormField>
                  
                  <FormField
                    label={<span className="field-label">Name of the Employee (Arabic) <span className="required">*</span></span>}
                    errorText={errors.serviceEmployeeNameAr}
                  >
                    <Input
                      value={formData.serviceEmployeeNameAr}
                      onChange={({ detail }) => handleInputChange('serviceEmployeeNameAr', detail.value)}
                      placeholder="أدخل اسم الموظف بالعربية"
                      readOnly={!isEditMode}
                    />
                  </FormField>
                </Grid>
                
                <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                  <FormField
                    label={<span className="field-label">Employee CPR Number</span>}
                    errorText={errors.serviceEmployeeCpr}
                  >
                    <Input
                      value={formData.serviceEmployeeCpr}
                      onChange={({ detail }) => handleInputChange('serviceEmployeeCpr', detail.value)}
                      placeholder="Enter CPR number"
                      readOnly={!isEditMode}
                    />
                  </FormField>
                  
                  <FormField
                    label={<span className="field-label">Position of the Employee</span>}
                    errorText={errors.serviceEmployeePosition}
                  >
                    <Input
                      value={formData.serviceEmployeePosition}
                      onChange={({ detail }) => handleInputChange('serviceEmployeePosition', detail.value)}
                      placeholder="Enter employee position"
                      readOnly={!isEditMode}
                    />
                  </FormField>
                </Grid>
                
                <Header variant="h3">Employee Contact Details</Header>
                
                <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                  <FormField
                    label={<span className="field-label">Office No</span>}
                    errorText={errors.serviceEmployeeOffice}
                  >
                    <Input
                      value={formData.serviceEmployeeOffice}
                      onChange={({ detail }) => handleInputChange('serviceEmployeeOffice', detail.value)}
                      placeholder="Enter office number"
                      readOnly={!isEditMode}
                    />
                  </FormField>
                  
                  <FormField
                    label={<span className="field-label">Mobile No</span>}
                    errorText={errors.serviceEmployeeMobile}
                  >
                    <Input
                      value={formData.serviceEmployeeMobile}
                      onChange={({ detail }) => handleInputChange('serviceEmployeeMobile', detail.value)}
                      placeholder="+97339000xxxx or 39000xxxx or 101"
                      readOnly={!isEditMode}
                    />
                  </FormField>
                  
                  <FormField
                    label={<span className="field-label">Email Address</span>}
                    errorText={errors.serviceEmployeeEmail}
                  >
                    <Input
                      value={formData.serviceEmployeeEmail}
                      onChange={({ detail }) => handleInputChange('serviceEmployeeEmail', detail.value)}
                      placeholder="Enter email address"
                      readOnly={!isEditMode}
                    />
                  </FormField>
                </Grid>
              </SpaceBetween>
            </Container>

            {/* Save Button */}
            {isEditMode && (
              <Box textAlign="right">
                <SpaceBetween direction="horizontal" size="xs">
                  {profileExists && (
                    <Button 
                      variant="normal"
                      onClick={() => {
                        setIsEditMode(false);
                        loadProfileData(); // Reload original data
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button 
                    variant="primary"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner size="normal" /> Submitting...
                      </>
                    ) : (
                      profileExists ? 'Save Changes' : 'Create Profile'
                    )}
                  </Button>
                </SpaceBetween>
              </Box>
            )}
              </>
            )}
      </SpaceBetween>
    </Container>
  );
};

export default InstituteProfileForm;